import { useSearch } from "@tanstack/react-router";
import { Tag } from "antd";
import { useGetCascadeLogsExportMutation, useGetCascadeLogsQuery } from "api/TenantSlug/Processes/hooks";
import { CascadeLogDetailDrawer } from "components/Drawers/CascadeLogDetailDrawer/CascadeLogDetailDrawer";
import { cascadeProcessItemStatusColorMap } from "components/Tables/CascadeLogTable/cascadeProcessItemStatusColorMap";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import { formatCascadeLogDuration } from "./formatCascadeLogDuration";

import GetCascadeLogsResponse = App.Http.Responses.Cascade.GetCascadeLogsResponse;
import CascadeLogData = App.Data.CascadeLogData;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface SearchParams {
    [`cascadeLogDetail-id`]: string;
}

const HIDDEN_STATUS = "unused";

export const CascadeLogTable = () => {
    const tableId = TableId.CascadeLogTable;
    const { t } = usePaldockTranslation("tables.cascade-log");
    const { t: tG } = usePaldockTranslation("tables.general-columns");

    const { t: tEnum } = usePaldockTranslation("general");
    const tableAtomInstance = tableAtom<CascadeLogData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const permissionsUtils = usePermission();
    const createdAtStr = useCreatedAtFilterValue();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const userFilter = tableState.filter?.filter ?? {};
    const userStatusFilter = userFilter.status as Record<string, unknown> | undefined;

    const buildStatusFilter = (): Record<string, unknown> => {
        if (!userStatusFilter) return { not_in: [HIDDEN_STATUS] };

        const { not: userNot, not_in: userNotIn, ...userStatusFilterRest } = userStatusFilter;
        const excludedStatuses = new Set<string>([HIDDEN_STATUS]);
        if (userNot !== undefined) excludedStatuses.add(String(userNot));
        if (userNotIn !== undefined) {
            (Array.isArray(userNotIn) ? userNotIn : [userNotIn]).forEach((value) =>
                excludedStatuses.add(String(value))
            );
        }

        return { ...userStatusFilterRest, not_in: [...excludedStatuses] };
    };

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...userFilter,
                status: buildStatusFilter(),
                ...(userFilter.created_at
                    ? {}
                    : {
                          created_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? "-created_at",
            include:
                "conversionStateSend,conversionStateSend.owner,conversionStateSend.advertiser,conversionStateSend.offer,cascade,integration",
        },
    };

    const getCascadeLogsQuery = useGetCascadeLogsQuery(request, { enabled: false });
    const getCascadeLogsExportMutation = useGetCascadeLogsExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetCascadeLogsResponse,
        void,
        CascadeLogData,
        BaseExportRequest
    >(tableId, getCascadeLogsQuery, undefined, undefined, getCascadeLogsExportMutation);

    const { getRowHandlers, getRowClassName } = useRowHandlers<CascadeLogData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.CascadeLogDetail,
                drawerParam: `${DrawerId.CascadeLogDetail}-id`,
                permission: PermissionEnum.CascadeLogView,
            },
        ],
    });

    const columnConfig: TableColumnType<CascadeLogData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "conversionStateSend.origin_eid",
            key: "conversionStateSend.origin_eid",
            render: (_, row) =>
                row.conversionStateSend?.origin_eid ? (
                    <ShortenedIdWithCopy id={row.conversionStateSend.origin_eid} />
                ) : (
                    <EmptyCell />
                ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionStateSend.origin_eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "conversionStateSend.eid",
            key: "conversionStateSend.eid",
            render: (_, row) =>
                row.conversionStateSend?.eid ? <ShortenedIdWithCopy id={row.conversionStateSend.eid} /> : <EmptyCell />,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionStateSend.eid",
            ellipsis: true,
            width: 150,
        },
        TableColumn.Entity.Owner<CascadeLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            dataIndex: "conversionStateSend.owner.nickname",
            key: "conversionStateSend.owner.nickname",
            filterName: "conversionStateSend.owner_id",
            sortName: "conversionStateSend.owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionStateSend?.owner,
        }),
        TableColumn.Entity.Advertiser<CascadeLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            dataIndex: "conversionStateSend.advertiser.nickname",
            key: "conversionStateSend.advertiser.nickname",
            filterType: FILTER_TYPES.advertiserSelect,
            filterName: "conversionStateSend.advertiser_id",
            sortName: "conversionStateSend.advertiser.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionStateSend?.advertiser,
        }),
        TableColumn.Entity.Offer<CascadeLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            dataIndex: "conversionStateSend.offer.title",
            key: "conversionStateSend.offer.title",
            sortName: "conversionStateSend.offer.title",
            filterName: "conversionStateSend.offer_id",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionStateSend?.offer,
        }),
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.channel")} />,
            dataIndex: "cascade.title",
            key: "cascade.title",
            render: (_, row) => <EntityLinkCell.Cascade entity={row.cascade} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.cascadeSelect,
            filterName: "cascade_id",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.integration")} />,
            dataIndex: "integration.name",
            key: "integration.name",
            render: (_, row) =>
                row.integration ? <EntityLinkCell.Integration entity={row.integration} /> : <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "integration.name",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.duration")} />,
            dataIndex: "duration_ms",
            key: "duration_ms",
            render: (_, row) => formatCascadeLogDuration(row.duration_ms) ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "duration_ms",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "status",
            key: "status",
            render: (_, row) => {
                const status = row.status;
                if (!status) return <EmptyCell />;
                const cfg = cascadeProcessItemStatusColorMap[status] ?? { color: "default" };
                return (
                    <Tag icon={"icon" in cfg ? cfg.icon : undefined} color={cfg.color}>
                        {tEnum(`cascade_process_item_status.${status}`)}
                    </Tag>
                );
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "status",
            filterOptions: [
                { label: tEnum("cascade_process_item_status.completed"), value: "completed" },
                { label: tEnum("cascade_process_item_status.failed"), value: "failed" },
                { label: tEnum("cascade_process_item_status.rejected"), value: "rejected" },
                { label: tEnum("cascade_process_item_status.declined"), value: "declined" },
                { label: tEnum("cascade_process_item_status.processing"), value: "processing" },
                { label: tEnum("cascade_process_item_status.waiting"), value: "waiting" },
                { label: tEnum("cascade_process_item_status.next_waiting"), value: "next_waiting" },
                { label: tEnum("cascade_process_item_status.filtered"), value: "filtered" },
            ],
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
            dataIndex: "message",
            key: "message",
            render: (message, row) => (
                <StepLogMessageCell message={message} id={`cascade-log-message-${row.itemId}`} lines={4} />
            ),
            ellipsis: true,
            width: 220,
        },
    ];

    return (
        <>
            <Table<GetCascadeLogsResponse, CascadeLogData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetCascadeLogsResponse}
                dataSource={tableFetchDataUtils.tableData?.logs ?? []}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.itemId as Key}
                onRow={(record, rowIndex) => getRowHandlers({ ...record, id: record.itemId }, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        filter: request.filter,
                        extension: exportState.extension,
                        headers: exportState.headers,
                        page: request.page,
                        export_all: exportState.range === "all",
                        delimiter: exportState.delimiter,
                    })
                }
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"created_at"}
                dateFilterMode={"minute"}
                autoScrollWidth={true}
                hideActionsColumn={true}
            />

            {permissionsUtils.hasPermission(PermissionEnum.CascadeLogView) &&
                searchParams[`${DrawerId.CascadeLogDetail}-id`] && (
                    <CascadeLogDetailDrawer logId={searchParams[`${DrawerId.CascadeLogDetail}-id`]} />
                )}
        </>
    );
};
